import type {
  AddressDto,
  CreateOrderDto,
  DiscountDto,
  ExpireDateResponseDto,
  HttpExceptionDto,
  OrderDto,
  OrderItemDto,
  OrderWideDto,
  OrganizationDto,
  ProductDto,
  ProductWideDto,
  RenewUpgradePossibilityDto,
  StripeSessionDto,
  UserDto,
  UpgradeProductDto,
} from '@/castapi';
import { loadStripe } from '@stripe/stripe-js/pure';
import { getDiscountApi, getErrorMessage, getOrdersApi, getProductsApi, isKnownError } from '@/castapi/helpers';
import { AppLogger } from '@/logger';
import type { AxiosResponse } from 'axios';
import type { IActionParams, RootGetters, RootState } from '@/store/modules';
import type { Commit, Dispatch } from 'vuex/types/index.d.ts';
import { router, apiConfig, PRODUCT_IDS } from '@/utils';
import snackbarPlugin from '@/utils/plugins/snackbar';

const logger = new AppLogger('OrderState');

const getSseEventSource = (accessToken: string, clientId: string) =>
  new EventSource(`${apiConfig.basePath}/api/single/order-notifications/events?token=${accessToken}&id=${clientId}`);

export interface RenewUpgradePossibilityDtoWithNested
  extends Omit<RenewUpgradePossibilityDto, 'upgradeProducts' | 'renewProducts'> {
  upgradeProducts: ProductDto[];
  renewProducts: ProductDto[];
}

export interface CartItemData extends OrderItemDto, UpgradeProductDto {}

export type CreateOrderDongleRefAndAddressesPart = {
  existingDongleRef?: number;
  existingCloudProductRef?: number;
  shippingAddress?: Partial<AddressDto> & {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    companyName: string;
    organizationRef: number;
    userRef: number;
  };
  billingAddress?: Partial<AddressDto> & {
    firstName?: string;
    lastName?: string;
  };
  dealerPurchaseRequest?: boolean;
  shippingDisabled?: boolean;
  hideInvoiceFromUser?: boolean;
  freeLicenseReasonRef?: number;
  freeLicenseReasonDetails?: string;
};

export interface IExpireDateParams {
  steppingRef: number;
  expiryDate: string;
  purchaseOption: number;
  duration: number;
  itemCount: number;
  fromTrial: boolean;
}

export interface IOrderState {
  order?: Partial<OrderWideDto>;
  finishedOrder?: OrderDto;
  finishedOrderDiscount?: DiscountDto;
  discount?: DiscountDto;
  error?: string;
  isLoading: boolean;
  checkoutLoading: boolean;
  renewUpgrade: RenewUpgradePossibilityDtoWithNested;
  //renewUpgradeCloud: RenewUpgradePossibilityDtoWithNested;
  productsMap: Record<string, ProductWideDto[]>;
  productLoadError?: string;
  productsLoading: boolean;
  shopProducts: ProductWideDto[];
  expireDateLoading: boolean;
  expireDateLoadError?: string;
  expireDate?: ExpireDateResponseDto;
  isTrialAvailableForOrganization: boolean;
  isTrialAvailabilityLoading: boolean;
  fromTrial?: boolean;
  eventSource?: EventSource;
}

// We haven't guarantee that corresponding products loaded at the moment when we call this function!
const getOrderItemProduct = (orderItem: OrderItemDto, allProducts: ProductDto[]): undefined | CartItemData => {
  const product = allProducts.find(p => p.productId === orderItem.itemProductRef);
  return product ? ({ ...product, ...orderItem } as CartItemData) : undefined;
};

const getOrderValue = <T>(...values: T[]): T => {
  for (const v of values) {
    if (v) {
      return v;
    }
  }
  return undefined as unknown as T;
};

// Plan: Solve problem - all fields can contains only value or undefined, but we trying to assign undefined!
const getCreateOrderDto = (
  state: IOrderState,
  { isAdmin, organization: org, user }: { isAdmin: boolean; organization: OrganizationDto; user: UserDto },
  data?: CreateOrderDongleRefAndAddressesPart,
): CreateOrderDto => {
  const { shippingAddress, billingAddress, existingDongleRef, existingCloudProductRef } = { ...data };
  const order = state.order;
  if (!order) {
    throw new Error('Order is not defined');
  }
  return {
    orderId: getOrderValue(order.orderId),
    firstName: getOrderValue(shippingAddress?.firstName, order.firstName, !isAdmin && user.firstName) as string,
    lastName: getOrderValue(shippingAddress?.lastName, order.lastName, !isAdmin && user.lastName) as string,
    address1: getOrderValue(shippingAddress?.address1, order.address1),
    apt: getOrderValue(shippingAddress?.apt, order.apt),
    city: getOrderValue(shippingAddress?.city, order.city),
    province: getOrderValue(shippingAddress?.province, order.province),
    countryRef: getOrderValue(shippingAddress?.countryRef, order.countryRef),
    postalCode: getOrderValue(shippingAddress?.postalCode, order.postalCode),
    phoneNumber: getOrderValue(shippingAddress?.phoneNumber, order.phoneNumber, org?.organizationPhoneNumber),
    companyName: getOrderValue(shippingAddress?.companyName, order.companyName, org?.organizationName) as string,
    organizationRef: getOrderValue(
      shippingAddress?.organizationRef,
      order.organizationRef,
      org?.organizationId,
    ) as number,
    billingAddress1: getOrderValue(billingAddress?.address1, order.billingAddress1) as string,
    billingApt: getOrderValue(billingAddress?.apt, order.billingApt),
    billingCity: getOrderValue(billingAddress?.city, order.billingCity) as string,
    billingPostalCode: getOrderValue(billingAddress?.postalCode, order.billingPostalCode) as string,
    billingCountryRef: getOrderValue(billingAddress?.countryRef, order.billingCountryRef),
    billingProvince: getOrderValue(billingAddress?.province, order.billingProvince),
    billingFirstName: getOrderValue(
      billingAddress?.firstName,
      order.billingFirstName,
      !isAdmin && user.firstName,
    ) as string,
    billingLastName: getOrderValue(
      billingAddress?.lastName,
      order.billingLastName,
      !isAdmin && user.lastName,
    ) as string,
    vatNumber: getOrderValue(billingAddress?.vatNumber, order.vatNumber),
    orderItems: order.orderItems as OrderItemDto[],
    refPromo: getOrderValue(state.discount?.discountId, order.refPromo),
    userRef: getOrderValue(shippingAddress?.userRef, order.userRef, !isAdmin && user?.userId) as number,
    freeLicenseReasonRef: order.freeLicenseReasonRef,
    freeLicenseReasonDetails: order.freeLicenseReasonDetails,
    notes: getOrderValue(order.notes),
    isRenewUpgrade: Boolean(
      !state.fromTrial &&
        (existingDongleRef || order.existingDongleRef || existingCloudProductRef || order.existingCloudProductRef),
    ),
    dealerPurchaseRequest: Boolean(data?.dealerPurchaseRequest || order?.dealerPurchaseRequest),
    existingDongleRef:
      existingDongleRef !== undefined
        ? existingDongleRef
        : (!existingCloudProductRef && order.existingDongleRef) || undefined,
    existingCloudProductRef:
      existingCloudProductRef !== undefined
        ? existingCloudProductRef
        : (!existingDongleRef && order.existingCloudProductRef) || undefined,
    shippingDisabled: Boolean(data?.shippingDisabled),
    hideInvoiceFromUser: Boolean(data?.hideInvoiceFromUser),
  } as CreateOrderDto;
};

const validateOrder = (order?: Partial<OrderWideDto>): boolean =>
  Boolean(
    order?.orderItems?.length &&
      order.firstName &&
      order.lastName &&
      order.userRef &&
      order.billingCountryRef &&
      order.billingProvince &&
      order.companyName &&
      order.organizationRef &&
      (order.isRenewUpgrade
        ? order.existingDongleRef || order.existingCloudProductRef
        : order.isCloud || (order.address1 && order.city && order.province && order.countryRef && order.postalCode)),
  );

const renewUpgradeInitial = (): RenewUpgradePossibilityDtoWithNested => ({
  renewRequired: false,
  isTooNewForRenew: false,
  isTooOldForRenew: false,
  renewalsAvailable: {} as { [productId: number]: number },
  expireDateAfterMaxPossibleRenew: '',
  isUpgradePossible: false,
  renewProducts: [],
  upgradeProducts: [],
});

const initialState: () => IOrderState = () => ({
  order: {
    orderItems: [],
    orderId: undefined,
  },
  finishedOrder: undefined,
  finishedOrderDiscount: undefined,
  discount: undefined,
  error: undefined,
  isLoading: false,
  checkoutLoading: false,
  renewUpgrade: renewUpgradeInitial(),
  productsMap: {},
  productLoadError: undefined,
  productsLoading: false,
  shopProducts: [],
  expireDateLoading: false,
  expireDateLoadError: undefined,
  expireDate: undefined,
  freeLicenseReasonDetails: undefined,
  isTrialAvailableForOrganization: false,
  isTrialAvailabilityLoading: false,
  fromTrial: false,
});

export interface Getters {
  orderId?: number;
  order: OrderDto;
  allProductsForFinishedOrder: ProductDto[];
  allProducts: ProductWideDto[];
  cartProducts: CartItemData[];
  currentProductRenewUpgradeOptions: RenewUpgradePossibilityDtoWithNested;
  shopProducts: ProductWideDto[];
  orderProductsPrice: number;
  tax: number;
  shipping: number;
  orderDiscountTotal: number;
  discount?: DiscountDto;
  freeLicenseReasonRef?: number;
  freeLicenseReasonDetails?: string;
  isTrialAvailable: boolean;
}

type ActionParams = IActionParams<IOrderState, Getters>;

export default class {
  state = initialState;
  mutations = {
    LOADING(state: IOrderState): void {
      state.isLoading = true;
      state.error = undefined;
    },
    CHECKOUT_LOADING(state: IOrderState): void {
      state.checkoutLoading = true;
    },
    CHECKOUT_LOADED(state: IOrderState): void {
      state.checkoutLoading = false;
    },
    ERROR(state: IOrderState, payload: Error): void {
      state.checkoutLoading = false;
      state.isLoading = false;
      state.error = getErrorMessage(payload);
    },
    LOADED(state: IOrderState): void {
      state.isLoading = false;
    },
    ORDER_LOADED(state: IOrderState, order: OrderWideDto): void {
      state.isLoading = false;
      state.order = { ...state.order, ...order };
      if (!order.discount && state.discount) {
        state.discount = undefined;
      }
    },
    ORDER_CHANGED(state: IOrderState): void {
      state.isLoading = false;
      state.order = undefined;
    },
    CLEAR_ORDER(state: IOrderState): void {
      state.order = initialState().order;
      state.discount = initialState().discount;
      state.error = initialState().error;
    },
    CLEAR_FINISHED_ORDER(state: IOrderState): void {
      state.finishedOrder = initialState().finishedOrder;
      state.finishedOrderDiscount = initialState().finishedOrderDiscount;
    },
    FINISHED_ORDER_LOADED(state: IOrderState, order: OrderDto): void {
      state.isLoading = false;
      state.error = undefined;
      state.finishedOrder = order;
      state.finishedOrderDiscount = undefined;
    },
    FINISHED_ORDER_DISCOUNT_LOADED(state: IOrderState, discount: DiscountDto): void {
      state.isLoading = false;
      state.error = undefined;
      state.finishedOrderDiscount = discount;
    },
    DISCOUNT_LOADED(
      state: IOrderState,
      {
        discount,
        freeLicenseReasonRef,
        freeLicenseReasonDetails,
      }: { discount: DiscountDto; freeLicenseReasonRef?: number; freeLicenseReasonDetails?: string },
    ): void {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      state.isLoading = false;
      state.discount = discount;
      state.order.freeLicenseReasonRef = freeLicenseReasonRef;
      state.order.freeLicenseReasonDetails = freeLicenseReasonDetails;
      state.order.refPromo = discount?.discountId || undefined;
    },
    DROP_DISCOUNT(state: IOrderState): void {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      state.discount = undefined;
      state.order.refPromo = undefined;
      if (state.order.freeLicenseReasonRef !== undefined) {
        state.order.freeLicenseReasonRef = undefined;
        state.order.freeLicenseReasonDetails = undefined;
      }
    },
    REMOVE_PRODUCT_FROM_CART(state: IOrderState, product: ProductWideDto): void {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      if (!state.order.orderItems) {
        throw new Error('Order items are not defined');
      }
      state.order.orderItems = state.order.orderItems.filter(item => item.itemProductRef !== product.productId);
    },
    ADD_PRODUCT_TO_CART(
      state: IOrderState,
      data: { addedProduct: ProductWideDto; isAdmin: boolean; isTrialAvailable: boolean },
    ): void {
      const { addedProduct, isAdmin, isTrialAvailable } = data;
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      state.fromTrial = false;
      const orderItems = state.order.orderItems;
      if (!orderItems) {
        throw new Error('Order items are not defined');
      }
      const cartItem = orderItems.find((item: OrderItemDto) => item.itemProductRef === addedProduct.productId);
      const isTrial = isTrialAvailable && orderItems.length === 0 && addedProduct.productId === PRODUCT_IDS.WPLC1;
      const isAmnestyProduct = [PRODUCT_IDS.MARD, PRODUCT_IDS.MCRP].includes(addedProduct.productId);
      if (!cartItem) {
        orderItems.push({
          itemCount: 1,
          itemId: undefined,
          itemOrderRef: undefined,
          itemPrice: addedProduct.productPrice,
          itemProductRef: addedProduct.productId,
          itemDiscount: 0,
          itemTotalPrice: addedProduct.productPrice,
          itemAutoRenew: (isAmnestyProduct || !isAdmin) && !isTrial,
        });
      } else {
        cartItem.itemCount++;
      }
    },
    CHANGE_PRODUCT_COUNT(state: IOrderState, { itemCount, productId }: { itemCount: number; productId: number }): void {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      const orderItems = state.order.orderItems;
      if (!orderItems) {
        throw new Error('Order items are not defined');
      }
      const cartItem = orderItems.find((item: OrderItemDto) => item.itemProductRef === productId);
      if (!cartItem) {
        throw new Error(`Can not find product with id: ${productId}`);
      }
      cartItem.itemCount = itemCount;
    },
    CHANGE_AUTO_RENEW(
      state: IOrderState,
      { itemAutoRenew, productId }: { itemAutoRenew: boolean; productId: number },
    ): void {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      const orderItems = state.order.orderItems;
      if (!orderItems) {
        throw new Error('Order items are not defined');
      }
      const cartItem = orderItems.find((item: OrderItemDto) => item.itemProductRef === productId);
      if (!cartItem) {
        throw new Error(`Can not find product with id: ${productId}`);
      }
      cartItem.itemAutoRenew = itemAutoRenew;
    },
    PRODUCTS_LOADED(state: IOrderState, productsMap: Record<string, ProductWideDto[]>): void {
      state.productsMap = productsMap;
      state.shopProducts = Object.keys(productsMap).reduce((acc, curr): ProductWideDto[] => {
        const reduced = productsMap[curr].reduce(
          (poAcc: ProductWideDto[], currProduct: ProductWideDto): ProductWideDto[] => {
            const mapped = currProduct.productOptions.map(option => ({ ...currProduct, ...option }));
            return [...poAcc, ...mapped];
          },
          [],
        );
        return [...acc, ...reduced];
      }, new Array<ProductWideDto>());
      state.productsLoading = false;
    },
    PRODUCTS_LOADING(state: IOrderState): void {
      state.productLoadError = undefined;
      state.productsLoading = true;
    },
    PRODUCTS_LOAD_ERROR(state: IOrderState, error: Error): void {
      state.productLoadError = getErrorMessage(error);
      state.productsLoading = false;
    },
    SET_FROM_TRIAL(state: IOrderState, fromTrial: boolean): void {
      state.fromTrial = fromTrial;
    },
    SELECT_PRODUCT(state: IOrderState, product: ProductWideDto | CartItemData): void {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      const isAmnestyProduct = [PRODUCT_IDS.MARD, PRODUCT_IDS.MCRP].includes(product.productId);
      state.order.orderItems = [
        {
          itemCount: (product as CartItemData).itemCount || 1,
          itemId: undefined,
          itemOrderRef: undefined,
          itemPrice: 0,
          itemProductRef: product.productId,
          itemDiscount: 0,
          itemTotalPrice: 0,
          itemAutoRenew: isAmnestyProduct || Boolean((product as CartItemData).itemAutoRenew),
        },
      ];
    },
    RENEW_UPGRADE_PRODUCTS_LOADING(state: IOrderState): void {
      state.isLoading = true;
      state.error = undefined;
    },
    RENEW_UPGRADE_PRODUCTS_LOADED(state: IOrderState, payload: RenewUpgradePossibilityDtoWithNested): void {
      state.isLoading = false;
      state.renewUpgrade = payload;
    },
    EXPIRE_DATE_LOADING(state: IOrderState): void {
      state.expireDateLoading = true;
      state.expireDate = undefined;
      state.expireDateLoadError = undefined;
    },
    EXPIRE_DATE_LOADED(state: IOrderState, expireDate: ExpireDateResponseDto): void {
      state.expireDateLoading = false;
      state.expireDate = expireDate;
    },
    EXPIRE_DATE_LOAD_ERROR(state: IOrderState, error: Error): void {
      state.expireDateLoading = false;
      state.expireDateLoadError = getErrorMessage(error);
    },
    TRIAL_AVAILABILITY_LOADING(state: IOrderState): void {
      state.isTrialAvailabilityLoading = true;
    },
    TRIAL_AVAILABILITY_LOADED(state: IOrderState, isTrialAvailable: boolean): void {
      state.isTrialAvailabilityLoading = false;
      state.isTrialAvailableForOrganization = isTrialAvailable;
    },
    TRIAL_AVAILABILITY_LOAD_ERROR(state: IOrderState): void {
      state.isTrialAvailabilityLoading = false;
      state.isTrialAvailableForOrganization = false;
    },
    HANDLE_MESSAGE(): void {
      //NEEDED FOR SUBSCRIPTION
    },
    SSE_CONNECTED(state: IOrderState, eventSource: EventSource): void {
      state.eventSource = eventSource;
    },
    SSE_DISCONNECTED(state: IOrderState): void {
      state.eventSource = undefined;
    },
    // SET_FREE_LICENSE_REASON(
    //   state: IOrderState,
    //   {
    //     freeLicenseReasonRef,
    //     freeLicenseReasonDetails,
    //   }: {
    //     freeLicenseReasonRef: number;
    //     freeLicenseReasonDetails: string;
    //   },
    // ): void {
    //   if (!state.order) {
    //     throw new Error('Order is not defined');
    //   }
    //   state.order.freeLicenseReasonRef = freeLicenseReasonRef;
    //   state.order.freeLicenseReasonDetails = freeLicenseReasonDetails;
    // },
  };
  actions = {
    async redirectToCheckout(
      {
        state,
        commit,
        rootGetters,
      }: {
        state: IOrderState;
        commit: Commit;
        rootGetters: RootGetters;
      },
      paymentFailed = false,
    ): Promise<void> {
      const orderId = state.order?.orderId;
      try {
        commit('CHECKOUT_LOADING');
        const token = rootGetters['login/accessToken'];
        const fromTrial = !state.order.isRenewUpgrade && state.order.existingCloudProductRef;
        if (fromTrial && !paymentFailed) {
          await getOrdersApi(token).ordersControllerUpgradeTrialOrder({ orderId });
          commit('CHECKOUT_LOADED');
          await router.push({ name: 'shop-receipt', params: { orderId: orderId.toString() } });
          return;
        }
        const isTrial = state.order.orderItems.find(orderItem => orderItem.isTrial);
        const response = isTrial
          ? await getOrdersApi(token).ordersControllerCreateSetupSessionForOrder({ orderId })
          : await getOrdersApi(token).ordersControllerCreateSession({ orderId });
        const session = response.data as StripeSessionDto;
        commit('CLEAR_ORDER');
        commit('CHECKOUT_LOADED');
        if (session?.url) {
          window.location.href = session.url as string;
        }
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('redirectToCheckout', error);
      }
    },

    async redirectToCheckoutByOrderId(
      {
        commit,
        rootGetters,
      }: {
        state: IOrderState;
        commit: Commit;
        rootGetters: RootGetters;
      },
      orderId: number,
    ): Promise<void> {
      try {
        commit('CHECKOUT_LOADING');
        const token = rootGetters['login/accessToken'];
        const response = await getOrdersApi(token).ordersControllerCreateSession({ orderId, fromEmail: true });
        const session = response.data as StripeSessionDto;
        commit('CHECKOUT_LOADED');
        if (session?.url) {
          window.location.href = session.url as string;
        }
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('redirectToCheckoutByOrderId', error);
      }
    },

    async authenticatePayment({ commit, rootGetters }: ActionParams, orderId: number): Promise<void> {
      commit('LOADING');
      try {
        const response = await getOrdersApi(rootGetters['login/accessToken']).ordersControllerGetIntentClientSecret(
          orderId,
        );
        const paymentIntentClientSecret = response.data;
        if (!paymentIntentClientSecret) {
          await router.push({ name: '404' });
        }
        const stripe = await loadStripe(import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY as string);
        if (!stripe) {
          throw new Error('Can not init Stripe');
        }
        const result = await stripe.confirmCardPayment(paymentIntentClientSecret);
        if (result.error) {
          throw new Error(result.error.message);
        } else {
          commit('LOADED');
          await router.push({ name: 'shop-receipt', params: { orderId: orderId.toString() } });
        }
      } catch (error) {
        if ((error as Error & { status: number }).status === 404) {
          await router.push({ name: '404' });
        }
        commit('ERROR', error);
      }
    },

    async getOrder({ commit, dispatch, rootGetters }: ActionParams, orderId: number): Promise<void> {
      commit('LOADING');
      try {
        const response = await getOrdersApi(rootGetters['login/accessToken']).ordersControllerGetOrder(orderId);
        await dispatch('orderLoaded', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getOrder', { orderId });
      }
    },

    async syncOrder(
      { commit, state, rootGetters }: { commit: Commit; state: IOrderState; rootGetters: RootGetters },
      data?: CreateOrderDongleRefAndAddressesPart,
    ): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      // Note: User can add product to cart without login, but in this case we don't need to send order to server
      if (!accessToken) {
        return;
      }
      const isAdmin = rootGetters['login/isAdmin'];
      const organization = isAdmin
        ? rootGetters['adminOrganizations/currentOrganization']
        : rootGetters['organizations/organization'];
      const user = isAdmin ? rootGetters['adminOrganizations/currentOrganizationMainUser'] : rootGetters['login/user'];
      try {
        const orderToSync = getCreateOrderDto(state, { isAdmin, organization, user }, data);
        commit('LOADING');
        const response = await getOrdersApi(accessToken).ordersControllerAddOrder(orderToSync);
        if (state.discount) {
          if (response.data.discount) {
            snackbarPlugin.success(`Discount code "${state.discount?.promoCode}" applied successfully`);
          } else if (!response.data.discount) {
            snackbarPlugin.warn("Discount code isn't valid");
          }
        }
        commit('ORDER_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('syncOrder', error, { data });
      }
    },

    async orderLoaded(
      { commit, dispatch, getters }: { commit: Commit; dispatch: Dispatch; getters: Getters },
      order: OrderWideDto,
    ): Promise<void> {
      if (order.refPromo && !getters.discount) {
        await dispatch('loadDiscount', order.refPromo);
      }
      if (order.existingDongleRef) {
        await dispatch('getDongleRenewUpgradeOptions', order.existingDongleRef);
      }
      if (order.existingCloudProductRef) {
        await dispatch('getCloudProductRenewUpgradeOptions', order.existingCloudProductRef);
      }
      commit('ORDER_LOADED', order);
    },

    async getFinishedOrder({ commit, dispatch, rootGetters }: ActionParams, orderId: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      if (!accessToken) {
        return;
      }
      commit('LOADING');
      try {
        const response = await getOrdersApi(accessToken).ordersControllerGetUserOrder(orderId);
        commit('FINISHED_ORDER_LOADED', response.data);
        const discountId = (response.data as OrderDto)?.refPromo;
        if (discountId) {
          await dispatch('getFinishedOrderDiscount', discountId);
        }
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getFinishedOrder', error, { orderId });
      }
    },

    async getFinishedOrderDiscount({ commit, rootGetters }: ActionParams, discountId: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      if (!accessToken) {
        return;
      }
      try {
        commit('LOADING');
        const response = await getDiscountApi(accessToken).discountsControllerFindOne(discountId);
        commit('FINISHED_ORDER_DISCOUNT_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getFinishedOrderDiscount', error, { discountId });
      }
    },

    async applyDiscount(
      { state, commit, dispatch, rootGetters }: ActionParams,
      {
        discountCode,
        freeLicenseReasonRef,
        freeLicenseReasonDetails,
      }: { discountCode: string; freeLicenseReasonRef?: number; freeLicenseReasonDetails?: string },
    ): Promise<void> {
      if (!discountCode) {
        return;
      }
      const accessToken = rootGetters['login/accessToken'];
      if (!accessToken) {
        return;
      }
      try {
        commit('DROP_DISCOUNT');
        commit('LOADING');
        const order = state.order as OrderWideDto;
        if (!order) {
          throw new Error('Order is not defined');
        }

        const orderId: number = order.orderId;
        const response = await getDiscountApi(accessToken).discountsControllerFindByCode(
          discountCode,
          orderId,
          order.dealerPurchaseRequest,
        );
        if (!response.data) {
          snackbarPlugin.warn("Discount code isn't valid");
        }
        await dispatch('discountLoaded', {
          discount: response.data,
          freeLicenseReasonRef,
          freeLicenseReasonDetails,
        });
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('applyDiscount', error, { discountCode });
      }
    },

    async discountLoaded(
      { state, commit, dispatch }: ActionParams,
      {
        discount,
        freeLicenseReasonRef,
        freeLicenseReasonDetails,
      }: { discount: DiscountDto; freeLicenseReasonRef?: number; freeLicenseReasonDetails?: string },
    ): Promise<void> {
      commit('DISCOUNT_LOADED', { discount, freeLicenseReasonRef, freeLicenseReasonDetails });
      if (state.order) {
        await dispatch('syncOrder');
      }
    },

    async dropDiscount({ state, commit, dispatch }: ActionParams): Promise<void> {
      commit('DROP_DISCOUNT');
      if (state.order) {
        await dispatch('syncOrder');
      }
    },

    async loadDiscount({ commit, rootGetters }: ActionParams, discountId: number): Promise<void> {
      const accessToken = rootGetters['login/accessToken'];
      if (!accessToken) {
        return;
      }
      try {
        commit('LOADING');
        const response = await getDiscountApi(accessToken).discountsControllerFindOne(discountId);
        commit('DISCOUNT_LOADED', { discount: response.data });
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('loadDiscount', error, { discountId });
      }
    },

    async changeOrderNotes({ state, commit, rootGetters }: ActionParams, notes: string): Promise<void> {
      if (!state.order) {
        return;
      }
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('LOADING');
        if (!state.order) {
          throw new Error('Order is not defined');
        }
        const response = await getOrdersApi(accessToken).ordersControllerChangeOrderNotes({
          orderId: (state.order as OrderWideDto).orderId,
          notes,
        });
        commit('ORDER_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('changeOrderNotes', error, { notes });
      }
    },

    async markAsPaid(
      { state, commit, rootGetters }: ActionParams,
      data: { isFulfilled: boolean; paymentMethod: string; notes: string },
    ): Promise<void> {
      if (!state.order) {
        return;
      }
      const { isFulfilled, paymentMethod, notes } = data;
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('LOADING');
        if (!state.order) {
          throw new Error('Order is not defined');
        }
        const response = await getOrdersApi(accessToken).ordersControllerMarkOrderAsPaid({
          orderId: (state.order as OrderWideDto).orderId,
          isFulfilled,
          paymentMethod,
          notes,
        });
        commit('ORDER_CHANGED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('markAsPaid', error, { isFulfilled, paymentMethod, notes });
      }
    },

    async updateFulfillmentStatus({ state, commit, rootGetters }: ActionParams, isFulfilled: boolean): Promise<void> {
      if (!state.order) {
        return;
      }
      try {
        const accessToken = rootGetters['login/accessToken'];

        commit('LOADING');
        if (!state.order) {
          throw new Error('Order is not defined');
        }
        const response = await getOrdersApi(accessToken).ordersControllerUpdateOrderFulfillmentStatus({
          orderId: (state.order as OrderWideDto).orderId,
          isFulfilled,
        });
        commit('ORDER_CHANGED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('markAsPaid', error, { isFulfilled });
      }
    },

    async deleteOrder({ state, commit, rootGetters }: ActionParams): Promise<void> {
      if (!state.order) {
        return;
      }
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('LOADING');
        const orderId = state.order.orderId;
        const response = await getOrdersApi(accessToken).ordersControllerDeleteOrder({ orderId });
        commit('ORDER_CHANGED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('deleteOrder', error, { orderId: state.order?.orderId });
      }
    },

    async voidOrder({ state, commit, rootGetters }: ActionParams): Promise<void> {
      if (!state.order) {
        return;
      }
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('LOADING');
        const orderId = state.order.orderId;
        const response = await getOrdersApi(accessToken).ordersControllerVoidOrder({ orderId });
        commit('ORDER_CHANGED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('voidOrder', error, { orderId: state.order?.orderId });
      }
    },

    async removePaymentIntentDataFromOrder({ commit, rootGetters }: ActionParams, orderId: number): Promise<void> {
      try {
        const accessToken = rootGetters['login/accessToken'];
        commit('LOADING');
        const response = await getOrdersApi(accessToken).ordersControllerRemovePaymentIntentDataFromOrder({ orderId });
        commit('ORDER_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('removePaymentIntentDataFromOrder', error, { orderId });
      }
    },

    async removeProductFromCart({ commit, dispatch }: ActionParams, product: ProductWideDto): Promise<void> {
      commit('REMOVE_PRODUCT_FROM_CART', product);
      await dispatch('syncOrder');
    },

    async getProductsFromApi({ commit, dispatch, rootGetters }: ActionParams, showOutdated = false): Promise<void> {
      try {
        const isEducational = rootGetters['login/isEducational'];
        commit('PRODUCTS_LOADING');
        const response = showOutdated
          ? await getProductsApi(rootGetters['login/accessToken']).productsControllerCumulativeProductsWithOutdated()
          : await getProductsApi().productsControllerCumulativeProducts(isEducational);
        commit('PRODUCTS_LOADED', response.data);
        await dispatch('getUserUnfinishedOrder');
      } catch (error) {
        commit('PRODUCTS_LOAD_ERROR', error);
        logger.captureStoreError('getProductsFromApi', error);
      }
    },

    async getUserUnfinishedOrder(
      { commit, dispatch, getters, rootGetters }: ActionParams,
      userId?: number,
    ): Promise<void> {
      try {
        const token = rootGetters['login/accessToken'];
        if (!token) {
          return;
        }
        if (getters.cartProducts.length) {
          await dispatch('syncOrder');
          return;
        }
        if (!getters.allProducts.length) {
          await dispatch('getProductsFromApi', true);
          return;
        }
        commit('LOADING');
        let response: AxiosResponse<OrderDto>;
        if (userId) {
          response = await getOrdersApi(token).ordersControllerGetUserUnfinishedOrder(userId);
        } else {
          if (rootGetters['login/isAdmin'] === undefined) {
            throw new Error('Need to check `isAdmin()` before call `getUnfinishedOrder()`');
          }
          if (rootGetters['login/isAdmin']) {
            throw new Error('`getUnfinishedOrder()` can be called only for customer');
          }
          response = await getOrdersApi(token).ordersControllerGetUnfinishedOrder();
        }
        await dispatch('orderLoaded', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getUserUnfinishedOrder', error, { userId });
      }
    },

    async addProductToCart(
      { state, commit, dispatch, rootGetters, getters }: ActionParams,
      addedProduct: ProductWideDto,
    ): Promise<void> {
      if (state.order?.existingDongleRef) {
        commit('CLEAR_ORDER');
      }
      const isAdmin = rootGetters['login/isAdmin'];
      const isTrialAvailable = getters.isTrialAvailable;
      commit('ADD_PRODUCT_TO_CART', { addedProduct, isAdmin, isTrialAvailable });
      await dispatch('syncOrder');
    },

    async addTrialToCart({ commit, getters, dispatch }: ActionParams): Promise<void> {
      commit('CLEAR_ORDER');
      const trialProduct = getters.allProducts.find(p => p.canBeTrial);
      commit('ADD_PRODUCT_TO_CART', { addedProduct: trialProduct, isTrialAvailable: true });
      await dispatch('syncOrder');
    },

    async changeProductCount({ commit, dispatch }: ActionParams, cartItem: CartItemData): Promise<void> {
      commit('CHANGE_PRODUCT_COUNT', { itemCount: cartItem.itemCount, productId: cartItem.productId });
      await dispatch('syncOrder');
    },

    async changeAutoRenew({ commit, dispatch }: ActionParams, cartItem: CartItemData): Promise<void> {
      commit('CHANGE_AUTO_RENEW', { itemAutoRenew: cartItem.itemAutoRenew, productId: cartItem.productId });
      await dispatch('syncOrder');
    },

    async getDongleRenewUpgradeOptions({ commit, rootGetters }: ActionParams, dongleId: number): Promise<void> {
      const token = rootGetters['login/accessToken'];
      commit('RENEW_UPGRADE_PRODUCTS_LOADING');
      try {
        const response = await getProductsApi(token).productsControllerGetRenewUpgradeProducts(dongleId);
        commit('RENEW_UPGRADE_PRODUCTS_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        // Plan: check if it is a correct situation?
        if (isKnownError(error as HttpExceptionDto, 403, 'No permissions to fetch dongle info')) {
          return;
        }
        logger.captureStoreError('getDongleRenewUpgradeOptions', error, { dongleId });
      }
    },

    async getCloudProductRenewUpgradeOptions(
      { commit, rootGetters }: ActionParams,
      cloudProductId: number,
    ): Promise<void> {
      const token = rootGetters['login/accessToken'];
      commit('RENEW_UPGRADE_PRODUCTS_LOADING');
      try {
        const response = await getProductsApi(token).productsControllerGetRenewUpgradeCloudProducts(cloudProductId);
        commit('RENEW_UPGRADE_PRODUCTS_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        // Plan: check if it is a correct situation?
        if (isKnownError(error as HttpExceptionDto, 403, 'No permissions to fetch cloud product info')) {
          return;
        }
        logger.captureStoreError('getCloudProductRenewUpgradeOptions', error, { cloudProductId });
      }
    },

    async selectProduct(
      { commit, dispatch, rootGetters }: ActionParams,
      { product, fromTrial = false }: { product: ProductWideDto; fromTrial?: boolean },
    ): Promise<void> {
      commit('SET_FROM_TRIAL', fromTrial);
      commit('SELECT_PRODUCT', product);
      const isAdmin = rootGetters['login/isAdmin'];
      const existingDongleRef = isAdmin
        ? rootGetters['adminDongles/currentDongleId']
        : rootGetters['dongles/currentDongleId'];
      const existingCloudProductRef = isAdmin
        ? rootGetters['adminCloudProducts/currentCloudProductId']
        : rootGetters['cloudProducts/currentCloudProductId'];
      await dispatch('syncOrder', { existingDongleRef, existingCloudProductRef });
    },

    async testCheckout({ state, commit, rootGetters }: ActionParams): Promise<void> {
      if (!state.order) {
        throw new Error('Order is not defined');
      }
      const orderId = state.order.orderId;
      try {
        commit('LOADING');
        const token = rootGetters['login/accessToken'];
        const response = await getOrdersApi(token).ordersControllerProcessTestOrder({ orderId });
        commit('CLEAR_ORDER');
        commit('LOADED');
        window.location.href = response.data;
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('testCheckout', error, { orderId });
      }
    },

    async getNewExpireDate(
      { commit, rootGetters }: ActionParams,
      { steppingRef, expiryDate, purchaseOption, duration, itemCount, fromTrial }: IExpireDateParams,
    ): Promise<void> {
      commit('EXPIRE_DATE_LOADING');
      try {
        const token = rootGetters['login/accessToken'];
        const response = await getProductsApi(token).productsControllerGetNewExpireDate(
          steppingRef || -1,
          expiryDate,
          purchaseOption,
          duration,
          itemCount,
          fromTrial,
        );
        commit('EXPIRE_DATE_LOADED', response.data);
      } catch (error) {
        commit('EXPIRE_DATE_LOAD_ERROR', error);
        logger.captureStoreError('getNewExpireDate', error, {
          steppingRef,
          expiryDate,
          purchaseOption,
          duration,
          itemCount,
        });
      }
    },

    async setupPaymentMethodForProduct(
      { rootGetters }: ActionParams,
      data: { cloudProductId: number; dongleId: number; confirmationRequired: boolean },
    ): Promise<void> {
      try {
        const { cloudProductId, dongleId, confirmationRequired } = data;
        if (!cloudProductId && !dongleId) {
          throw new Error('Either `cloudProductId` or `dongleId` must be provided');
        }
        const token = rootGetters['login/accessToken'];
        const response = await getOrdersApi(token).ordersControllerCreateSetupSessionForProduct({
          cloudProductId,
          dongleId,
          confirmationRequired,
        });
        const session = response.data as StripeSessionDto;
        if (session?.url) {
          window.location.href = session.url as string;
        }
      } catch (error) {
        logger.captureStoreError('setupPaymentMethodForProduct', error);
      }
    },

    async getTrialAvailability({ commit, rootGetters }: ActionParams) {
      const accessToken = rootGetters['login/accessToken'];
      if (!accessToken) {
        commit('LOADED');
        return;
      }
      try {
        commit('TRIAL_AVAILABILITY_LOADING');
        const isAdmin = rootGetters['login/isAdmin'];
        const organization = isAdmin
          ? rootGetters['adminOrganizations/currentOrganization']
          : rootGetters['organizations/organization'];
        if (!organization) {
          commit('TRIAL_AVAILABILITY_LOAD_ERROR');
          return;
        }
        const response = await getOrdersApi(accessToken).ordersControllerIsTrialAvailableForOrganization(
          organization.organizationId,
        );
        commit('TRIAL_AVAILABILITY_LOADED', response.data);
      } catch (error) {
        commit('TRIAL_AVAILABILITY_LOAD_ERROR', error);
        logger.captureStoreError('getTrialAvailability', error);
      }
    },

    connectToSSE({ rootGetters, commit, dispatch }: ActionParams, clientId: string): void {
      try {
        const accessToken = rootGetters['login/accessToken'];
        const eventSource = getSseEventSource(accessToken, clientId);
        eventSource.onmessage = event => {
          const data = JSON.parse(event.data);
          commit('HANDLE_MESSAGE', data);
        };
        eventSource.addEventListener('token_not_valid', async () => {
          eventSource.close();
          await dispatch('login/refreshToken', undefined, { root: true });
          await dispatch('connectToSSE', clientId);
        });
        eventSource.onerror = () => {
          eventSource.close();
          setTimeout(() => dispatch('connectToSSE', clientId), 30000);
        };
        commit('SSE_CONNECTED', eventSource);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('connectToSSE', error);
      }
    },

    disconnectFromSSE({ state, commit }: ActionParams): void {
      if (state.eventSource) {
        state.eventSource.close();
      }
      commit('SSE_DISCONNECTED');
    },
  };
  getters = {
    order: (state: IOrderState): undefined | Partial<OrderWideDto> => state.order,
    orderId: (state: IOrderState): undefined | number => state.order?.orderId,
    orderProductsPrice: (state: IOrderState): number =>
      state.order?.orderItems?.reduce(
        (acc: number, curr: OrderItemDto) => acc + curr.itemCount * (curr.itemPrice || 0),
        0,
      ) || 0,
    orderProductsTotal: (state: IOrderState): number =>
      state.order?.orderItems?.reduce((acc, curr) => acc + curr.itemCount * (curr.itemTotalPrice || 0), 0) || 0,
    orderDiscountTotal: (state: IOrderState): number =>
      state.order?.orderItems?.reduce((acc, curr) => acc + curr.itemCount * (curr.itemDiscount || 0), 0) || 0,
    finishedOrder: (state: IOrderState): undefined | OrderDto => state.finishedOrder,
    finishedOrderProducts: (state: IOrderState, getters: Getters): CartItemData[] =>
      (state.finishedOrder?.orderItems
        ?.map(item => getOrderItemProduct(item, getters.allProductsForFinishedOrder))
        .filter(v => v) as CartItemData[]) || [],
    finishedOrderProductsTotal: (state: IOrderState): number =>
      state.finishedOrder?.orderItems?.reduce((acc, curr) => acc + curr.itemCount * (curr.itemTotalPrice || 0), 0) || 0,
    finishedOrderProductsPrice: (state: IOrderState): number =>
      state.finishedOrder?.orderItems?.reduce((acc, curr) => acc + curr.itemCount * (curr.itemPrice || 0), 0) || 0,
    isFinishedOrderRenewUpgrade: (state: IOrderState): boolean => Boolean(state.finishedOrder?.isRenewUpgrade),
    isFinishedOrderShippable: (state: IOrderState): boolean => Boolean(state.finishedOrder?.shippable),
    isFinishedOrderTrial: (state: IOrderState): boolean =>
      Boolean(state.finishedOrder?.orderItems?.some(item => item.isTrial)),
    isFinishedOrderPaid: (state: IOrderState): boolean => Boolean(state.finishedOrder?.isPaid),
    discountPromoCode: (state: IOrderState): undefined | string =>
      state.order?.discount ? state.discount?.promoCode : undefined, // checking `state.order?.discount` to ensure that discount is applied
    finishedOrderDiscountPromoCode: (state: IOrderState): undefined | string => state.finishedOrderDiscount?.promoCode,
    discount: (state: IOrderState): undefined | DiscountDto => state.discount,
    isLoading: (state: IOrderState): boolean => state.isLoading,
    checkoutLoading: (state: IOrderState): boolean => state.checkoutLoading,
    error: (state: IOrderState): undefined | string => state.error,
    cartProducts: (state: IOrderState, getters: Getters): CartItemData[] =>
      (state.order?.orderItems
        ?.map(item => getOrderItemProduct(item, getters.allProducts))
        .filter(v => v) as CartItemData[]) || [],
    cartProductsCount: (_state: IOrderState, getters: Getters): number =>
      getters.cartProducts.reduce((acc: number, curr: CartItemData) => acc + curr.itemCount, 0),
    productsLoading: (state: IOrderState): boolean => state.productsLoading || state.isTrialAvailabilityLoading,
    productsLoadError: (state: IOrderState): undefined | string => state.productLoadError,
    productTypes: (state: IOrderState): string[] => (state?.productsMap ? Object.keys(state.productsMap) : []),
    productsMap: (state: IOrderState): Record<string, ProductWideDto[]> => state.productsMap,
    productsList: (state: IOrderState): ProductWideDto[] => state.productsMap.CLOUD || [],
    productsOldMap: (state: IOrderState): Record<string, ProductWideDto[]> =>
      Object.keys(state.productsMap).reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: state.productsMap[curr].map(item => ({
            ...item,
            productOptions: item.productOptions.filter(option => option.duration === 12),
          })),
        }),
        {},
      ),
    shopProducts: (state: IOrderState): ProductWideDto[] => state.shopProducts,
    currentProductRenewUpgradeOptions: (state: IOrderState): ProductDto[] => [
      ...state.renewUpgrade.upgradeProducts,
      ...state.renewUpgrade.renewProducts,
    ],
    currentProductRenewOptions: (state: IOrderState): ProductDto[] => state.renewUpgrade.renewProducts,
    currentProductUpgradeOptions: (state: IOrderState): ProductDto[] => state.renewUpgrade.upgradeProducts,
    currentProductRenewRequired: (state: IOrderState): boolean => state.renewUpgrade.renewRequired,
    currentProductLicenseTooNewForRenew: (state: IOrderState): boolean => state.renewUpgrade.isTooNewForRenew,
    currentProductLicenseTooOldForRenew: (state: IOrderState): boolean => state.renewUpgrade.isTooOldForRenew,
    currentProductRenewalsAvailable: (
      state: IOrderState,
    ): number | { [productId: number]: { maxRenewalsAvailable: number; minRenewalsAvailable: number } } =>
      state.renewUpgrade.renewalsAvailable as
        | number
        | { [productId: number]: { maxRenewalsAvailable: number; minRenewalsAvailable: number } },
    currentProductExpireDateAfterMaxPossibleRenew: (state: IOrderState): string =>
      state.renewUpgrade.expireDateAfterMaxPossibleRenew,
    currentProductUpgradePossibility: (state: IOrderState): boolean => state.renewUpgrade.isUpgradePossible,
    allProducts: (state: IOrderState, getters: Getters): RenewUpgradePossibilityDtoWithNested | ProductWideDto[] => {
      if (state.order?.existingDongleRef || state.order?.existingCloudProductRef) {
        return getters.currentProductRenewUpgradeOptions;
      }
      return state.shopProducts;
    },
    allProductsForFinishedOrder: (
      state: IOrderState,
      getters: Getters,
    ): RenewUpgradePossibilityDtoWithNested | ProductWideDto[] =>
      state.finishedOrder?.existingDongleRef || state.finishedOrder?.existingCloudProductRef
        ? getters.currentProductRenewUpgradeOptions
        : getters.shopProducts,
    isOrderRenewUpgrade: (state: IOrderState): boolean =>
      Boolean(state.order?.existingDongleRef || state.order?.existingCloudProductRef),
    orderValid: (state: IOrderState): boolean => validateOrder(state.order),
    tax: (state: IOrderState): number => state.order?.tax || 0,
    shipping: (state: IOrderState): number => state.order?.shipping || 0,
    notes: (state: IOrderState): undefined | string => state.order?.notes,
    grandTotalPrice: (_state: IOrderState, getters: Getters): number =>
      getters.orderProductsPrice + getters.tax + getters.shipping - getters.orderDiscountTotal,
    expireDateLoading: (state: IOrderState): boolean => state.expireDateLoading,
    expireDateLoadError: (state: IOrderState): undefined | string => state.expireDateLoadError,
    expireDate: (state: IOrderState): undefined | ExpireDateResponseDto => state.expireDate,
    isOrderShippable: (_state: IOrderState, getters: Getters): boolean =>
      !_state.order?.shippingDisabled && getters.cartProducts.some((product: CartItemData) => product.shippable),
    freeLicenseReasonRef: (state: IOrderState): undefined | number => state.order?.freeLicenseReasonRef,
    isTrialAvailable: (
      state: IOrderState,
      _getters: Getters,
      _rootState: RootState,
      rootGetters: RootGetters,
    ): boolean => state.isTrialAvailableForOrganization || !rootGetters['login/loggedIn'],
    isTrialNotAvailableForCurrentOrder: (state: IOrderState, getters: Getters): boolean =>
      state.order?.orderItems.length === 1 &&
      state.order?.orderItems[0].itemProductRef === PRODUCT_IDS.WPLC1 &&
      !getters.isTrialAvailable,
    isFromTrial: (state: IOrderState): boolean =>
      !state.order?.isRenewUpgrade && Boolean(state.order?.existingCloudProductRef),
    isTrialAddedToCart: (_state: IOrderState, getters: Getters): boolean =>
      getters.cartProducts.some((product: CartItemData) => product.isTrial),
  };
}
